import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Award, AlertTriangle, Target, PieChart as PieChartIcon } from 'lucide-react';
import { InfoTooltip } from './SimpleTooltip';
import { usePortfolioInsights, formatInsightPercent } from './hooks/usePortfolioInsights';
import { PerformerCards } from './insights/PerformerCards';

const getScoreStyle = (score, thresholds = { high: 70, mid: 40 }) => {
  if (score >= thresholds.high) return { bg: 'bg-green-100', text: 'text-green-600' };
  if (score >= thresholds.mid) return { bg: 'bg-yellow-100', text: 'text-yellow-600' };
  return { bg: 'bg-red-100', text: 'text-red-600' };
};

const getDiversificationLabel = (score) => score >= 70 ? 'Well diversified' : score >= 40 ? 'Moderately diversified' : 'Consider diversifying';

const getRiskStyle = (level) => {
  if (level === 'Low') return { bg: 'bg-green-100', text: 'text-green-600' };
  if (level === 'Moderate') return { bg: 'bg-yellow-100', text: 'text-yellow-600' };
  return { bg: 'bg-red-100', text: 'text-red-600' };
};

const InsightCard = ({ label, tooltip, value, valueClass, icon: Icon, iconBg, iconColor, sub }) => (
  <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-slate-500 mb-1 flex items-center">{label}<InfoTooltip content={tooltip} /></p><p className={`text-2xl font-bold ${valueClass || ''}`}>{value}</p></div><div className={`p-3 rounded-full ${iconBg}`}><Icon className={`w-6 h-6 ${iconColor}`} /></div></div><p className="text-xs text-slate-400 mt-2">{sub}</p></CardContent></Card>
);

const PortfolioInsights = ({ investments }) => {
  const insights = usePortfolioInsights(investments);

  if (!insights) {
    return <Card><CardHeader><CardTitle className="flex items-center space-x-2"><Target className="w-5 h-5 text-slate-600" /><span>Portfolio Insights</span></CardTitle></CardHeader><CardContent><p className="text-slate-500 text-center py-8">Add investments to see portfolio insights</p></CardContent></Card>;
  }

  const scoreStyle = getScoreStyle(insights.diversificationScore);
  const riskStyle = getRiskStyle(insights.riskLevel);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <InsightCard label="Diversification" tooltip="Shows variety across investment types. Higher score means better diversification (0-100 scale)." value={insights.diversificationScore} icon={PieChartIcon} iconBg={scoreStyle.bg} iconColor={scoreStyle.text} sub={getDiversificationLabel(insights.diversificationScore)} />
        <InsightCard label="Avg. Return" tooltip="Average return percentage across all your investments." value={formatInsightPercent(insights.avgReturn)} valueClass={insights.avgReturn >= 0 ? 'text-green-600' : 'text-red-600'} icon={insights.avgReturn >= 0 ? TrendingUp : TrendingDown} iconBg={insights.avgReturn >= 0 ? 'bg-green-100' : 'bg-red-100'} iconColor={insights.avgReturn >= 0 ? 'text-green-600' : 'text-red-600'} sub="Across all investments" />
        <InsightCard label="Risk Level" tooltip="Based on your asset mix. Stocks and crypto are high risk, bonds and cash are low risk." value={insights.riskLevel} icon={AlertTriangle} iconBg={riskStyle.bg} iconColor={riskStyle.text} sub="Portfolio risk profile" />
        <InsightCard label="Win Rate" tooltip="Number of profitable investments compared to total investments." value={`${insights.profitableCount}/${insights.totalInvestments}`} icon={Award} iconBg="bg-blue-100" iconColor="text-blue-600" sub="Profitable investments" />
      </div>
      <PerformerCards topPerformer={insights.topPerformer} worstPerformer={insights.worstPerformer} />
    </div>
  );
};

export default PortfolioInsights;
